import React, { useState } from 'react';
import { 
    Modal, Form, Input, Select, Checkbox, Space, Alert, Divider,
    Button
} from 'antd';
import { 
    ShopOutlined, LinkOutlined, ApiOutlined, 
    CheckCircleOutlined, ExclamationCircleOutlined, SyncOutlined
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { testSellerWebhook } from '../../actions';

const { Option } = Select;

const EditSellerAccountModal = ({ 
    visible, 
    editingAccount, 
    onCancel, 
    onSave,
    loading 
}) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [webhookTestResult, setWebhookTestResult] = useState(null);
    const [formChanged, setFormChanged] = useState(false);

    // Reset form when visible changes or editingAccount changes
    React.useEffect(() => {
        if (visible) {
            form.resetFields();
            setWebhookTestResult(null);
            
            if (editingAccount) {
                form.setFieldsValue({
                    name: editingAccount.name,
                    sellerId: editingAccount.sellerId,
                    email: editingAccount.email,
                    marketplaces: editingAccount.marketplaces || [],
                    webhookUrl: editingAccount.webhookUrl,
                    status: editingAccount.status || 'active'
                });
            }
        }
    }, [visible, editingAccount, form]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            onSave(values);
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    const handleTestWebhook = async () => {
        try {
            const webhookUrl = form.getFieldValue('webhookUrl');
            if (!webhookUrl) {
                setWebhookTestResult({
                    success: false,
                    message: 'Vui lòng nhập URL webhook trước khi test'
                });
                return;
            }

            setWebhookTestResult({loading: true});
            const result = await dispatch(testSellerWebhook(webhookUrl));
            setWebhookTestResult(result);
        } catch (error) {
            setWebhookTestResult({
                success: false,
                message: error.message || 'Có lỗi xảy ra khi test webhook'
            });
        }
    };

    return (
        <Modal
            title={`${editingAccount ? 'Chỉnh sửa' : 'Thêm'} tài khoản Amazon Seller`}
            open={visible}
            onOk={handleSave}
            onCancel={onCancel}
            width={700}
            okText={editingAccount ? 'Cập nhật' : 'Thêm mới'}
            cancelText="Hủy"
        >
            <Form 
                form={form} 
                layout="vertical" 
                className="seller-account-form"
                onValuesChange={() => setFormChanged(true)}
            >
                <Form.Item
                    name="name"
                    label="Tên tài khoản"
                    rules={[{ required: true, message: 'Vui lòng nhập tên tài khoản' }]}
                >
                    <Input placeholder="Nhập tên tài khoản bán hàng" prefix={<ShopOutlined />} />
                </Form.Item>
                
                <Form.Item
                    name="sellerId"
                    label="Seller ID"
                    rules={[{ required: true, message: 'Vui lòng nhập Seller ID' }]}
                    tooltip="ID người bán trên Amazon (Merchant ID)"
                >
                    <Input placeholder="VD: A2ODUCQXK8BCRP" />
                </Form.Item>
                
                <Form.Item
                    name="email"
                    label="Email"
                    rules={[{ type: 'email', message: 'Email không hợp lệ' }]}
                >
                    <Input placeholder="Email liên kết với tài khoản" />
                </Form.Item>

                <Form.Item
                    name="marketplaces"
                    label="Marketplaces"
                    tooltip="Các thị trường mà tài khoản này hoạt động"
                >
                    <Checkbox.Group style={{ width: '100%' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {['US', 'UK', 'CA', 'DE', 'FR', 'IT', 'ES', 'JP', 'AU', 'AE', 'BR', 'MX', 'SG'].map(market => (
                                <Checkbox key={market} value={market}>
                                    {market}
                                </Checkbox>
                            ))}
                        </div>
                    </Checkbox.Group>
                </Form.Item>

                <Form.Item
                    name="status"
                    label="Trạng thái"
                    initialValue="active"
                >
                    <Select>
                        <Option value="active">Hoạt động</Option>
                        <Option value="inactive">Không hoạt động</Option>
                        <Option value="suspended">Tạm ngưng</Option>
                    </Select>
                </Form.Item>
                
                <Divider orientation="left">Cài đặt Webhook</Divider>
                
                <Form.Item
                    name="webhookUrl"
                    label={
                        <Space>
                            <span>Webhook URL</span>
                            <Button 
                                type="primary" 
                                size="small"
                                onClick={handleTestWebhook}
                                icon={<ApiOutlined />}
                            >
                                Test
                            </Button>
                        </Space>
                    }
                    tooltip="URL nhận thông báo từ Amazon khi có đơn hàng mới hoặc cập nhật"
                    extra="URL này sẽ nhận các thông báo push từ Amazon về đơn hàng, sản phẩm, v.v."
                >
                    <Input 
                        placeholder="VD: https://your-domain.com/api/webhooks/amazon" 
                        prefix={<LinkOutlined />}
                        disabled={webhookTestResult?.loading}
                    />
                </Form.Item>

                {webhookTestResult && !webhookTestResult.loading && (
                    <Alert
                        message={
                            webhookTestResult.success 
                                ? "Test thành công" 
                                : "Test không thành công"
                        }
                        description={webhookTestResult.message}
                        type={webhookTestResult.success ? "success" : "error"}
                        showIcon
                        icon={webhookTestResult.success ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                        style={{ marginBottom: 16 }}
                    />
                )}
                
                {webhookTestResult?.loading && (
                    <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
                        <SyncOutlined spin style={{ marginRight: 8 }} /> Đang test webhook...
                    </div>
                )}
            </Form>
        </Modal>
    );
};

export default EditSellerAccountModal;